import { Action, Dispatch } from 'redux';
import { BasePageModel } from 'models/BasePageModel';
import { LoadingStatus } from 'models/LoadingStatus';
import { IHref } from 'components/Shared/server/href';
import { historyPushState } from 'store/history/actions';
import { createHref, Service, handleServerError } from 'components/Shared/server';
import batchAction from 'store/batching';
import { currentScreenHeight, currentScrollPosition, tryToScrollTo } from './delayedScroll';
import { pageViews, ViewType } from 'views/registry';

export const APP_HYDRATED = '@@APP/HYDRATED';
export const APP_INIT = 'APP_INIT';
export const UPDATE_CURRENT_PAGE = 'UPDATE_CURRENT_PAGE';
export const SET_PAGELOADINGSTATUS = 'SET_PAGELOADINGSTATUS';
export const UPDATE_WEB_CATALOG = 'UPDATE_WEB_CATALOG';

export type UpdateCurrentPageAction = Action & {
  newPage: BasePageModel;
};
export type SetPageStatusAction = Action & {
  status: LoadingStatus;
};
export type UpdateWebCatalogAction = Action & {
  webCatalog: string;
};

export const updateCurrentWebCatalog = (webCatalog: string): UpdateWebCatalogAction => ({
  type: UPDATE_WEB_CATALOG,
  webCatalog
});

export const isCatalog = (
  newPage: Record<string, unknown> | BasePageModel
): newPage is BasePageModel => !!(newPage as BasePageModel).webCatalog;

export const hydratedAction = {
  type: APP_HYDRATED
} as const;

export const updateCurrentPage = <P extends BasePageModel>(newPage: P) => ({
  type: UPDATE_CURRENT_PAGE,
  newPage
});

export const setPageLoadingStatus = (status: LoadingStatus): SetPageStatusAction => ({
  type: SET_PAGELOADINGSTATUS,
  status
});

/**
 * Preload does not return a promise according to react-loadable, but it does.
 */
const preload = async (pageView: ViewType<unknown>): Promise<void> => {
  await (pageView.preload() as unknown as Promise<void>);
};

export const getPage =
  (
    href: IHref,
    options: { scrollTo?: false | [number, number]; emit?: false; screenHeight?: number } = {}
  ) =>
  async (dispatch: Dispatch) => {
    const { scrollTo = [0, 0], emit = true, screenHeight: oldScreenHeight = 0 } = options;
    const url = createHref(href);
    const scrollPosition = currentScrollPosition(window);
    const screenHeight = currentScreenHeight();
    dispatch(setPageLoadingStatus(LoadingStatus.Loading));

    try {
      const page = await Service.get<BasePageModel>({ url: url.toString() });

      const pageView = pageViews[page.componentName];
      if (pageView) {
        await preload(pageView);
      }

      dispatch(
        batchAction(
          emit && historyPushState({ url: href, scrollPosition, screenHeight }),
          updateCurrentPage(page),
          setPageLoadingStatus(LoadingStatus.Completed)
        )
      );

      if (scrollTo) {
        tryToScrollTo(scrollTo, oldScreenHeight);
      }

      return page;
    } catch (error) {
      return handleServerError(dispatch)(error);
    }
  };
